import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import {
  DEFAULT_LOCALIZATION_CONFIG,
  LocalizationConfig,
  _updateGlobalLocalizationConfig,
} from '../constants/localization';

interface LocalizationContextType {
  config: LocalizationConfig;
  cookieStatus: string | null;
  setLanguage: (language: string) => void;
  setCountry: (country: string) => void;
  setConfig: (config: LocalizationConfig) => void;
  setCookieStatus: (status: string | null) => void;
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

interface LocalizationProviderProps {
  children: ReactNode;
  initialConfig?: LocalizationConfig;
}

/**
 * Localization Provider Component
 *
 * This provider allows dynamic management of language and country settings.
 * Currently defaults to 'FI' for both, but can be extended to support
 * user preferences, localStorage persistence, or other dynamic sources.
 */
export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({
  children,
  initialConfig = DEFAULT_LOCALIZATION_CONFIG,
}) => {
  const [config, setConfigState] = useState<LocalizationConfig>(initialConfig);
  const [cookieStatus, setCookieStatusState] = useState<string | null>(null);

  // Update global config whenever local config changes
  useEffect(() => {
    _updateGlobalLocalizationConfig(config);
  }, [config]);

  const setLanguage = (language: string) => {
    setConfigState((prev) => ({ ...prev, language }));
  };

  const setCountry = (country: string) => {
    setConfigState((prev) => ({ ...prev, country }));
  };

  const setConfig = (newConfig: LocalizationConfig) => {
    setConfigState(newConfig);
  };

  const setCookieStatus = (status: string | null) => {
    setCookieStatusState(status);
  };

  const value: LocalizationContextType = {
    config,
    cookieStatus,
    setLanguage,
    setCountry,
    setConfig,
    setCookieStatus,
  };

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>;
};

/**
 * Hook to use localization context
 *
 * Returns the current language and country configuration along with
 * functions to update them. Falls back to default config if context
 * is not available.
 */
export const useLocalization = (): LocalizationContextType => {
  const context = useContext(LocalizationContext);

  if (!context) {
    // Fallback to default configuration when context is not available
    return {
      config: DEFAULT_LOCALIZATION_CONFIG,
      cookieStatus: null,
      setLanguage: () => console.warn('LocalizationProvider not found, cannot set language'),
      setCountry: () => console.warn('LocalizationProvider not found, cannot set country'),
      setConfig: () => console.warn('LocalizationProvider not found, cannot set config'),
      setCookieStatus: () => console.warn('LocalizationProvider not found, cannot set cookie status'),
    };
  }

  return context;
};
